/// <amd-module name="Build/Pipelines/Tabs/Pipelines/PipelinesTreeView" />
import * as React from "react";

import { BuildDefinition, BuildStatus, DefinitionQueueStatus, DefinitionReference, Folder } from "Build/Client/Contracts/Build";
import { FolderData, PipelineData } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IDeleteDefinitionProps } from "Build/Common/Commands/DeleteDefinition/Command.types";
import { INewFolderProps } from "Build/Common/Commands/NewFolder/Command.types";
import { IPermissionsProps } from "Build/Common/Commands/Permissions/Command.types";
import { ISaveDefinitionProps } from "Build/Common/Commands/SaveDefinition/Command.types";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { ProcessType } from "Build/Common/Library/Constants";
import { getBuildStatusIndicatorData } from "Build/Common/Library/Indicator";
import { formatPipelineRunName } from "Build/Common/Library/PipelineRunNameFormatter";
import { BuildRequiredPermissions, Security } from "Build/Common/Library/Security";
import { utoa } from "Build/Common/Library/String";

import { updateTreeStateSettings, updateTreeStateUrl } from "Build/Common/Library/StateHandler";
import { DefinitionFavoritesActionCreator } from "Build/Pipelines/Actions/Favorites";
import { BuildSignalRActionHub } from "Build/Pipelines/Actions/SignalR";
import { Performance } from "Build/Pipelines/Performance";
import * as PipelineActions from "Build/Pipelines/PipelineActions";
import { PipelinesStore } from "Build/Pipelines/PipelinesStore";
import * as Resources from "Build/Pipelines/Resources";
import { FilteredPipelineTreeItemProvider, IPipelineTreeItem, PipelineTreeItemProvider } from "Build/Pipelines/Tabs/Pipelines/PipelinesTreeItemProvider";

import { IFavoritesService } from 'Favorites/Common/FavoritesService';

import { getTFSPageData } from "Tfs/Platform/Page";

import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, css } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IStartRightExtensionService } from "VSS/Platform/StartRightExtension";

import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, StatusSize } from "VSSUI/Status";
import { ColumnMore, IMeasurementStyle, ITableColumn, TableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, ITreeRowDetails, Tree, renderTreeRow } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData } from "VSSUI/ZeroData";

export interface IPipelinesTreeViewProps extends IVssComponentProperties {
    store: PipelinesStore;
    signalRHub: BuildSignalRActionHub
    security: Security
    favoritesActionCreator: DefinitionFavoritesActionCreator;
    isPageLoad: boolean;
    treeState: string;
    rootPath: string;
}

export interface IPipelinesTreeViewState {
    loading: boolean;
    filtered: boolean;
}

export class PipelinesTreeView extends VssComponent<IPipelinesTreeViewProps, IPipelinesTreeViewState> {
    private _ttiMarked = false;

    constructor(props: IPipelinesTreeViewProps, context: IVssComponentContext) {
        super(props, context);

        this._store = props.store;

        this._treeItemProvider = new PipelineTreeItemProvider(this.context.pageContext, props.signalRHub, props.isPageLoad, props.store.isListFiltered(), this.props.rootPath, this.props.treeState);

        this.state = {
            loading: this._treeItemProvider.loadingRoot.value,
            filtered: this._store.isListFiltered()
        }
    }

    public render() {
        if (this.state.loading) {
            return (
                <Card className="flex-grow pipelines-minimum-card-height" contentProps={{ contentPadding: false }} >
                    <Spinner className="pipelines-filter-spinner" size={SpinnerSize.large} label={Resources.Loading} />
                </Card>
            );
        }

        const nameColumn: ITreeColumn<ObservableValue<IPipelineTreeItem>> = { id: "name", name: Resources.Name, width: -100, renderCell: this._renderTreeCell };
        const menuColumn = new ColumnMore(this._getMenuForItem, this._showMenuForItem);
        const favoriteColumn: ITreeColumn<ObservableValue<IPipelineTreeItem>> = {
            id: "favorite",
            width: new ObservableValue(2.625),
            widthStyle: IMeasurementStyle.REM,
            className: "favorite-cell",
            renderCell: this._renderFavoriteCell,
            ariaLabel: Resources.Favorite
        };

        if (this.state.filtered) {
            if (this._store.getPipelines().length === 0) {
                const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-results.png");
                return (
                    <Card className="flex-grow pipelines-minimum-card-height" contentProps={{ contentPadding: false }}>
                        <div className="pipelines-zero-data pipelines-whole-page-centered">
                            <ZeroData
                                imageAltText={Resources.NoResultsFound}
                                imagePath={image}
                                secondaryText={Resources.NoResultsFound}
                            />
                        </div>
                    </Card>
                );
            }
            else {
                const filteredProvider = new FilteredPipelineTreeItemProvider(this.context, this.props.rootPath, this._store.getPipelines());
                this._treeItemProvider.setFilteredProvider(filteredProvider);
                return (
                    <Card className="flex-grow bolt-table-card"
                        contentProps={{ contentPadding: false }}
                        titleProps={{ text: Resources.FilteredFolderView, size: TitleSize.Medium }}>
                        <Tree
                            ariaLabel={Resources.FilteredFolderView}
                            columns={[nameColumn, favoriteColumn, menuColumn]}
                            itemProvider={filteredProvider}
                            showLines={true}
                            singleClickActivation={true}
                            onActivate={this._onActivate}
                            onToggle={(event, treeItem: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                                filteredProvider.toggle(treeItem.underlyingItem);
                            }}
                            scrollable={false}
                        />
                    </Card>
                );
            }
        }
        else {
            this._treeItemProvider.setFilteredProvider(undefined);
        }

        let cardTitle: string = Resources.FolderView;
        if (this.props.rootPath !== "\\") {
            cardTitle = format(Resources.FolderViewForFolder, this.props.rootPath);
        }

        if (this._treeItemProvider.length === 0) {
            const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-results.png");
            return (
                <Card className="flex-grow bolt-table-card pipelines-minimum-card-height"
                    contentProps={{ contentPadding: false }}
                    titleProps={{ text: cardTitle, size: TitleSize.Medium }}
                    headerCommandBarItems={this._getHeaderCommandBarItems()}>
                    <div className="pipelines-zero-data pipelines-whole-page-centered">
                        <ZeroData
                            imagePath={image}
                            imageAltText={Resources.ZeroDataNoCreatePipelinesAltText}
                            secondaryText={Resources.ZeroDataNoCreatePipelinesPrimary}
                        />
                    </div>
                </Card>
            )
        }

        return (
            <Card className="flex-grow bolt-table-card"
                contentProps={{ contentPadding: false }}
                titleProps={{ text: cardTitle, size: TitleSize.Medium }}
                headerCommandBarItems={this._getHeaderCommandBarItems()}>
                <Tree
                    ariaLabel={cardTitle}
                    className="pipelines-tree"
                    columns={[nameColumn, favoriteColumn, menuColumn]}
                    itemProvider={this._treeItemProvider}
                    showLines={true}
                    singleClickActivation={true}
                    onActivate={this._onActivate}
                    onToggle={(event, treeItem: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                        this._treeItemProvider.toggle(treeItem.underlyingItem);
                        updateTreeStateUrl(this.context.pageContext, utoa(this._treeItemProvider.expandedFolders));
                    }}
                    scrollable={false}
                    renderRow={this._renderTreeRow}
                />
            </Card>
        );
    }

    public componentDidMount() {
        this._store.addListener(this._onStoreChanged);
        this._treeItemProvider.loadingRoot.subscribe(this._onLoadingChanged);

        this._treeItemProvider.loadRoot();
        this._store.fetchHasDeletedPipelines();
        this._markTTI();
    }

    public componentWillUnmount() {
        this._store.removeListener(this._onStoreChanged);
        this._treeItemProvider.loadingRoot.unsubscribe(this._onLoadingChanged);
        updateTreeStateSettings(this.context.pageContext, utoa(this._treeItemProvider.expandedFolders));
    }

    private _onActivate = (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>, treeRow: ITreeRow<ObservableValue<IPipelineTreeItem>>) => {
        if (treeRow.data.underlyingItem.data.value.pipeline && treeRow.data.underlyingItem.data.value.linkProps && treeRow.data.underlyingItem.data.value.linkProps.href) {
            const pageContext = this.context.pageContext;
            onClickFPS(pageContext, treeRow.data.underlyingItem.data.value.linkProps.href, true, event);
        }
        else if (treeRow.data.underlyingItem.data.value.folder) {
            this._treeItemProvider.toggle(treeRow.data.underlyingItem);
        }
    }

    private _onStoreChanged = () => {
        this.setState({
            filtered: this._store.isListFiltered(),
            loading: this._store.isListFiltered() ? this._store.isLoadingData() : this._treeItemProvider.loadingRoot.value
        });
    }

    private _onLoadingChanged = () => {
        this.setState({
            loading: this._treeItemProvider.loadingRoot.value
        });
        // flush out changes before marking TTI
        setTimeout(() => this._markTTI(), 0);
    }

    private _markTTI() {
        if (!this._ttiMarked && !this._treeItemProvider.loadingRoot.value) {
            this._ttiMarked = true;
            new Performance(this.context.pageContext).mark("foldersView");
        }
    }

    private _renderFavoriteCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITreeItemEx<ObservableValue<IPipelineTreeItem>>>, tableItem: ITreeItemEx<ObservableValue<IPipelineTreeItem>>): JSX.Element => {
        let cellContents = <span></span>;

        const favoritesService = this.context.pageContext.getService<IFavoritesService>("IFavoritesService");

        let alwaysVisible = false;
        const pipeline = tableItem.underlyingItem.data.value.pipeline;

        if (favoritesService.canUseFavorites() && pipeline) {
            if (pipeline.isFavorite) {
                alwaysVisible = true;
                const removeFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.props.favoritesActionCreator.unfavoriteDefinition({
                        id: pipeline.id,
                        name: pipeline.name,
                        uri: pipeline.uri
                    });
                    pipeline.isFavorite = false;
                    this._treeItemProvider.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                        const pipelineTreeItem = value.underlyingItem.data.value;
                        const treePipeline = pipelineTreeItem.pipeline;
                        if (treePipeline && treePipeline.id === pipeline.id) {
                            treePipeline.isFavorite = false;
                            value.underlyingItem.data.value = pipelineTreeItem;
                        }
                    });
                };

                cellContents = (
                    <div className="right-align flex-row">
                        <Button primary={false} className="favorite-icon-color" subtle={true} iconProps={{ iconName: "FavoriteStarFill" }} ariaLabel={Resources.RemoveFromMyFavorites} onClick={removeFavorite} excludeTabStop={true} />
                    </div>
                );
            }
            else {
                const addFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.props.favoritesActionCreator.favoriteDefinition({
                        id: pipeline.id,
                        name: pipeline.name,
                        uri: pipeline.uri
                    });
                    pipeline.isFavorite = true;
                    this._treeItemProvider.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                        const pipelineTreeItem = value.underlyingItem.data.value;
                        const treePipeline = pipelineTreeItem.pipeline;
                        if (treePipeline && treePipeline.id === pipeline.id) {
                            treePipeline.isFavorite = true;
                            value.underlyingItem.data.value = pipelineTreeItem;
                        }
                    });
                };

                cellContents = (
                    <div className="right-align flex-row">
                        <Button primary={false} subtle={true} iconProps={{ iconName: "FavoriteStar" }} ariaLabel={Resources.AddToMyFavorites} onClick={addFavorite} excludeTabStop={true} />
                    </div>
                );
            }
        }

        return (
            <TableCell className="bolt-table-cell-side-action pipelines-favorite-cell" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className={css("flex-column", "padding-right-4", !alwaysVisible && "bolt-table-cell-content-reveal")}>
                    {cellContents}
                </div>
            </TableCell>
        );
    }

    private _renderTreeRow = (rowIndex: number, item: ITreeItemEx<ObservableValue<IPipelineTreeItem>>, details: ITreeRowDetails<ObservableValue<IPipelineTreeItem>>) => {
        const nameColumn: ITreeColumn<ObservableValue<IPipelineTreeItem>> = { id: "name", name: Resources.Name, width: -100, renderCell: this._renderTreeCell };
        const menuColumn = new ColumnMore(this._getMenuForItem, this._showMenuForItem);
        const favoriteColumn: ITreeColumn<ObservableValue<IPipelineTreeItem>> = {
            id: "favorite",
            width: new ObservableValue(2.625),
            widthStyle: IMeasurementStyle.REM,
            className: "favorite-cell",
            renderCell: this._renderFavoriteCell,
            ariaLabel: Resources.Favorite
        };

        const data = item ? item.underlyingItem.data : undefined;
        const detailsCopy = {...details};

        if (data && data.value.isEmptyMarker) {
            detailsCopy.singleClickActivation = false;
        }

        // renderTreeRow expects the data being passed to have a linkProps value to allow right-click -> open in new tab.
        // because we are passing an ObservableValue<PipelineTreeItem>, the data doesn't have a linkProps attribute on the top level
        // so the TreeRow never finds the linkProps.
        // We are adding the linkProps attribute to the ObservableValue so that TreeRow can find it, even though it's sketchy
        if (data) {
            (data as any).linkProps = data?.value.linkProps;
        }

        return renderTreeRow(rowIndex, item, detailsCopy, [nameColumn, favoriteColumn, menuColumn], data);
    }

    private _renderTreeCell = (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ObservableValue<IPipelineTreeItem>>, treeItem: ITreeItemEx<ObservableValue<IPipelineTreeItem>>): JSX.Element => {
        const { underlyingItem } = treeItem;

        let content: JSX.Element =
            <Observer item={underlyingItem.data} >
                {() => {
                    if (underlyingItem.data.value.isLoading) {
                        return (
                            <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                                <Spinner size={SpinnerSize.small} className="margin-right-8" />
                                <span className="secondary-text">{Resources.Loading}</span>
                            </span>
                        )
                    }

                    if (underlyingItem.data.value.isEmptyMarker) {
                        const mappedPath = this._treeItemProvider.getMappedPath(underlyingItem.data.value.path);
                        const canCreatePipeline = this.props.security.hasFolderPermission(mappedPath, BuildRequiredPermissions.EditBuildDefinition);
                        const pageContext = this.context.pageContext;
                        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                        const startRightExtensionService = pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
                        const newPipelineLink = startRightExtensionService.getCreatePipelineUrlByPipelineFolder(pageContext, underlyingItem.data.value.path) ||
                                                linkingService.getNewDefinitionEditUrl(underlyingItem.data.value.path);

                        return (
                            <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                                <span className="secondary-text">
                                    {Resources.EmptyFolder}&nbsp;
                                    {canCreatePipeline && <FPSLink excludeTabStop href={newPipelineLink}>{Resources.EmptyFolderLink}</FPSLink>}
                                </span>
                            </span>
                        );

                    }

                    let text = underlyingItem.data.value.name;
                    let isDraft = false;
                    let secondaryText: string | undefined = undefined;
                    let secondaryTextLink: string | undefined = undefined;
                    let icon: JSX.Element | undefined = undefined;
                    if (underlyingItem.data.value.folder) {
                        icon = <Icon iconName="FabricFolderFill" className="margin-right-8 pipelines-folder-icon" />

                        if (underlyingItem.data.value.folder.linkProps && underlyingItem.data.value.folder.linkProps.href) {
                            return (
                                <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                                    <Link 
                                        className="bolt-table-link bolt-table-inline-link flex-row flex-center text-ellipsis"
                                        excludeTabStop href={underlyingItem.data.value.folder.linkProps.href}
                                        ariaLabel={format(Resources.FolderAriaLabel, text)}
                                        removeUnderline={true}
                                    >
                                        {icon}
                                        <Tooltip overflowOnly={true}>
                                            <span className="inline-flex-row scroll-hidden pipeline-name">
                                                <span className="text-ellipsis">{text}</span>
                                            </span>
                                        </Tooltip>
                                    </Link>
                                </span>
                            );
                        }
                        else {
                            return (
                                <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                                    {icon}
                                    <Tooltip overflowOnly={true}>
                                        <span className="inline-flex-row scroll-hidden pipeline-name">
                                            <span className="text-ellipsis">{text}</span>
                                        </span>
                                    </Tooltip>
                                </span>
                            );
                        }
                    }
                    else if (underlyingItem.data.value.pipeline) {
                        const pipeline = underlyingItem.data.value.pipeline;
                        isDraft = pipeline.isDraft;
                        if (!this.state.filtered) {
                            this._treeItemProvider.fetchDetails(pipeline.id, rowIndex);
                        }

                        if (!this.state.filtered && !this._treeItemProvider.hasDetails(pipeline.id)) {
                            icon = (
                                <div className="margin-right-8">
                                    <Spinner size={SpinnerSize.small} />
                                </div>
                            );
                        }
                        else if (pipeline.latestRun) {
                            secondaryText = formatPipelineRunName(
                                pipeline.latestRun.number,
                                pipeline.latestRun.sourceVersionMessage,
                                pipeline.latestRun.appendCommitMessageToRunName
                            );
                            const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                            secondaryTextLink = linkingService.getBuildUrl(pipeline.latestRun.id);
                            const status = getBuildStatusIndicatorData(pipeline.latestRun.status, pipeline.latestRun.result);
                            icon = (
                                <div className="margin-right-8">
                                    <Tooltip text={status.label}>
                                        <span className="flex-row">
                                            <Status ariaLabel={status.statusProps.ariaLabel || status.label} size={StatusSize.m} {...status.statusProps} />
                                        </span>
                                    </Tooltip>
                                </div>
                            );
                        }
                        else {
                            secondaryText = Resources.NoRunsYet;
                            const status = getBuildStatusIndicatorData(BuildStatus.None);
                            icon = (
                                <div className="margin-right-8">
                                    <Tooltip text={status.label}>
                                        <span className="flex-row">
                                            <Status size={StatusSize.m} {...status.statusProps} />
                                        </span>
                                    </Tooltip>
                                </div>
                            );
                        }
                    }

                    return (
                        <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                            {icon}
                            <Tooltip overflowOnly={true}>
                                <span className="inline-flex-row scroll-hidden pipeline-name">
                                    <span className="text-ellipsis">{text}</span>
                                    {isDraft && <sup className="font-size-xxs pipelines-draft-indicator">{Resources.Draft}</sup>}
                                </span>
                            </Tooltip>
                            {secondaryText && secondaryTextLink &&
                                <span className="secondary-text text-ellipsis flex-row">
                                    <Tooltip overflowOnly={true}>
                                        <Link
                                            className="bolt-table-link bolt-table-inline-link text-ellipsis"
                                            excludeTabStop
                                            href={secondaryTextLink}
                                            removeUnderline={true}
                                        >
                                            {secondaryText}
                                        </Link>
                                    </Tooltip>
                                </span>
                            }
                            {secondaryText && !secondaryTextLink &&
                                <Tooltip overflowOnly={true}>
                                    <span className="secondary-text text-ellipsis margin-left-8">
                                        {secondaryText}
                                    </span>
                                </Tooltip>
                            }
                        </span>
                    );
                }}
            </Observer>;

        return ExpandableTreeCell({
            children: content,
            columnIndex,
            contentClassName: undefined,
            treeColumn,
            treeItem
        });
    }

    private _getHeaderCommandBarItems = (): IHeaderCommandBarItem[] => {
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (!tfsPageData || !tfsPageData.project || !this.props.security.isMember()) {
            return [];
        }
        const projectId = tfsPageData.project.id;
        return [
            {
                id: "root-new-folder",
                text: Resources.NewFolder,
                important: true,
                onActivate: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout((onDismiss) => {
                        const props: INewFolderProps = {
                            pageContext: this.context.pageContext,
                            projectId: projectId,
                            parentPath: this.props.rootPath,
                            onClose: onDismiss,
                            onFolderCreated: (folder: Folder) => {
                                this._treeItemProvider.folderCreated(folder);
                                if (this._treeItemProvider.length === 1) {
                                    this.setState({loading: false});
                                }
                                announce(format(Resources.FolderCreated, folder.path));
                            }
                        };
                        return React.createElement(CommandComponent, {
                            commandType: "ciNewFolderComponent",
                            ...props
                        }, null)
                    });
                }
            }
        ];
    }

    private _getMenuForItem = (item: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
        const folder = item.underlyingItem.data.value.folder;
        const pipeline = item.underlyingItem.data.value.pipeline;
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (!tfsPageData || !tfsPageData.project || !this.props.security.isMember()) {
            return {
                id: "folder-menu",
                items: []
            };
        }
        const projectId = tfsPageData.project.id;
        if (folder) {
            return this._getMenuForFolder(folder, projectId);
        }
        else if (pipeline) {
            return this._getMenuForPipeline(pipeline, projectId);
        }
        else {
            return {
                id: "folder-menu",
                items: []
            };
        }
    }

    private _getMenuForFolder = (folder: FolderData, projectId: string) => {
        const pageContext = this.context.pageContext;

        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const startRightExtensionService = pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
        const newPipelineLink = startRightExtensionService.getCreatePipelineUrlByPipelineFolder(pageContext, folder.path) ||
                                linkingService.getNewDefinitionEditUrl(folder.path);

        const menuItems = [];

        menuItems.push({
            text: Resources.Rename,
            id: "folder-menu-rename",
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    const props: ISaveDefinitionProps = {
                        pageContext: this.context.pageContext,
                        folder: {
                            path: folder.path
                        },
                        projectId: projectId,
                        saveAsTemplate: false,
                        onClose: onDismiss,
                        onFolderSaved: (folder: Folder, previousPath: string) => {
                            this._treeItemProvider.folderRenamed(previousPath, folder.path);
                            announce(format(Resources.FolderRenamed, previousPath, folder.path));
                        }
                    };
                    return React.createElement(CommandComponent, {
                        commandType: "ciSaveDefinitionComponent",
                        ...props
                    }, null)
                });
            }
        });

        const mappedPath = this._treeItemProvider.getMappedPath(folder.path);
        if (this.props.security.hasFolderPermission(mappedPath, BuildRequiredPermissions.EditBuildDefinition)) {
            menuItems.push({
                text: Resources.NewPipeline,
                id: "folder-menu-new-pipeline",
                href: newPipelineLink,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    onClickFPS(pageContext, newPipelineLink, true, event);
                }
            });
        }

        menuItems.push({
            text: Resources.NewFolder,
            id: "folder-menu-new-folder",
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    const props: INewFolderProps = {
                        pageContext: this.context.pageContext,
                        projectId: projectId,
                        parentPath: folder.path,
                        onClose: onDismiss,
                        onFolderCreated: (folder: Folder) => {
                            this._treeItemProvider.folderCreated(folder);
                            announce(format(Resources.FolderCreated, folder.path));
                        }
                    };
                    return React.createElement(CommandComponent, {
                        commandType: "ciNewFolderComponent",
                        ...props
                    }, null)
                });
            }
        });

        menuItems.push({
            text: Resources.Security,
            id: "folder-menu-security",
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    // This is supported by a legacy dialog that doesn't expose a close notification,
                    // so onDismiss is ignored and the wrapping elements will be left in the tree
                    const props: IPermissionsProps = {
                        path: folder.path
                    };
                    return React.createElement(CommandComponent, {
                        commandType: "ciPermissionsComponent",
                        ...props
                    }, null)
                });
            }
        });

        menuItems.push({
            text: Resources.Delete,
            id: "folder-menu-delete",
            onActivate: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    const props: IDeleteDefinitionProps = {
                        pageContext: this.context.pageContext,
                        path: folder.path,
                        projectId: projectId,
                        onClose: onDismiss,
                        onFolderDeleted: (path: string) => {
                            this._treeItemProvider.folderDeleted(path);
                            this._store.fetchHasDeletedPipelines(true);
                            announce(format(Resources.FolderDeleted, path));
                            if (this._treeItemProvider.length === 0) {
                                this.setState({loading: false});
                            }
                        }
                    };
                    return React.createElement(CommandComponent, {
                        commandType: "ciDeleteDefinitionComponent",
                        ...props
                    }, null)
                });
            }
        });

        const contributedItems = getMenuItems(this.context.pageContext, "ms.vss-build-web.pipelines-folder-menu", folder);
        menuItems.push(...contributedItems.value);

        return {
            id: "folder-menu",
            items: menuItems
        };
    }

    private _getMenuForPipeline = (pipeline: PipelineData, projectId: string) => {
        const mappedPath = this._treeItemProvider.getMappedPath(pipeline.path);
        const path = mappedPath ?? pipeline.path;
        const canEditDefinition: boolean = this.props.security.canEditDefinition({ id: pipeline.id, path: path } as DefinitionReference);
        const canQueueBuild: boolean = this.props.security.canQueueBuild({ id: pipeline.id, path: path } as DefinitionReference);
        const canDeleteDefinition: boolean = this.props.security.hasDefinitionPermission({ id: pipeline.id, path: path } as DefinitionReference, BuildRequiredPermissions.DeleteBuildDefinition);

        const pageContext = this.context.pageContext;
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const editLink = linkingService.getDefinitionEditUrl(pipeline.id);
        const editYamlLink = linkingService.getYamlPipelineEditUrl(pipeline.id);

        const isYamlProcess = pipeline.processType === ProcessType.Yaml;
        const isDisabled = pipeline.queueStatus === DefinitionQueueStatus.Disabled;

        const menuItems = [];

        menuItems.push(PipelineActions.GetEditViewMenuItem(pageContext, canEditDefinition, isYamlProcess ? editYamlLink : editLink));

        if (canQueueBuild && !isDisabled) {
            menuItems.push(PipelineActions.GetRunMenuItem(pageContext, projectId, pipeline.id));
        }

        menuItems.push(PipelineActions.GetSecurityMenuItem(pageContext, projectId, pipeline.id));

        if (canEditDefinition) {
            menuItems.push(PipelineActions.GetRenameMoveMenuItem(pageContext, projectId, pipeline.id, (updatedDefinition: BuildDefinition) => {
                if (updatedDefinition.path !== pipeline.path) {
                    this._treeItemProvider.pipelineMoved(updatedDefinition.id, updatedDefinition.path, updatedDefinition.name);
                }
                else {
                    this._treeItemProvider.pipelineRenamed(updatedDefinition.id, updatedDefinition.name);
                }
            }));
        }

        if (canDeleteDefinition) {
            menuItems.push(PipelineActions.GetDeleteMenuItem(pageContext, projectId, pipeline.id, definition => {
                let ids: number[] = [definition.id, ...definition.drafts.map(x => x.id)];
                ids.forEach(id => this._treeItemProvider.pipelineDeleted(id));
                if (this._treeItemProvider.length === 0) {
                    this.setState({loading: false});
                }
            }));
        }

        return {
            id: "folder-menu",
            items: menuItems
        };
    }

    private _showMenuForItem = (item: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
        return this.props.security.isMember() && (item.underlyingItem.data.value.folder !== undefined || item.underlyingItem.data.value.pipeline !== undefined);
    }

    private _store: PipelinesStore;
    private _treeItemProvider: PipelineTreeItemProvider;
}
